export const OTA_BRIDGE_VERSION = "0.6.0";

const semverPattern = /^(\d+)\.(\d+)\.(\d+)$/;
const sha256Pattern = /^[0-9a-f]{64}$/;

function parseSemanticVersion(version) {
  const match = String(version ?? "").match(semverPattern);
  return match ? match.slice(1).map(Number) : null;
}

export function compareFirmwareVersions(left, right) {
  const leftParts = parseSemanticVersion(left);
  const rightParts = parseSemanticVersion(right);
  if (!leftParts || !rightParts) return null;
  for (let index = 0; index < leftParts.length; index += 1) {
    if (leftParts[index] !== rightParts[index]) return leftParts[index] - rightParts[index];
  }
  return 0;
}

export function firmwareSupportsOta(version) {
  const comparison = compareFirmwareVersions(version, OTA_BRIDGE_VERSION);
  return comparison !== null && comparison >= 0;
}

function invalidCatalog() {
  return new Error("Talos received an invalid firmware catalog.");
}

function validateRelease(candidate) {
  if (!candidate || typeof candidate !== "object") throw invalidCatalog();
  const { hardware, label, version, chip_names: chipNames, chip_prefixes: chipPrefixes, flash, ota, ota_available: otaAvailable = true } = candidate;
  if (typeof hardware !== "string" || hardware.length < 3 || hardware.length > 64) throw invalidCatalog();
  if (typeof label !== "string" || label.length < 3 || label.length > 80 || !parseSemanticVersion(version)) throw invalidCatalog();
  if (!Array.isArray(chipNames) || !chipNames.every((value) => typeof value === "string" && value.length > 0)) throw invalidCatalog();
  if (!Array.isArray(chipPrefixes) || !chipPrefixes.every((value) => typeof value === "string" && value.length > 0)) throw invalidCatalog();
  if (!flash || !["keep", "dio", "qio"].includes(flash.mode) || typeof flash.frequency !== "string" || typeof flash.size !== "string" || !Array.isArray(flash.images) || flash.images.length < 1) throw invalidCatalog();
  const images = flash.images.map((image) => {
    if (!image || typeof image.path !== "string" || !image.path.startsWith("/firmware/") || !Number.isInteger(image.address) || image.address < 0) throw invalidCatalog();
    return { path: image.path, address: image.address };
  });
  if (typeof otaAvailable !== "boolean") throw invalidCatalog();
  if (!ota || typeof ota.path !== "string" || !ota.path.startsWith("/firmware/") || !Number.isInteger(ota.size) || ota.size < 1 || typeof ota.sha256 !== "string" || !sha256Pattern.test(ota.sha256)) throw invalidCatalog();
  if (!images.some((image) => image.address === 0x10000 && image.path === ota.path)) throw invalidCatalog();
  return {
    hardware,
    label,
    version,
    chipNames: chipNames.map((name) => name.toUpperCase()),
    chipPrefixes: chipPrefixes.map((prefix) => prefix.toUpperCase()),
    flashMode: flash.mode,
    flashFrequency: flash.frequency,
    flashSize: flash.size,
    images,
    ota: { path: ota.path, size: ota.size, sha256: ota.sha256 },
    otaAvailable,
  };
}

export async function loadFirmwareCatalog(fetcher = fetch) {
  const response = await fetcher("/firmware/catalog.json", { cache: "no-store" });
  if (!response.ok) throw new Error("Talos could not load the firmware catalog. Check your connection and try again.");
  const catalog = await response.json();
  if (!catalog || catalog.schema_version !== 1 || !Array.isArray(catalog.releases) || catalog.releases.length < 1) throw invalidCatalog();
  return catalog.releases.map(validateRelease);
}

export function findFirmwareRelease(catalog, hardware) {
  return catalog.find((release) => release.hardware === hardware) ?? null;
}

export async function resolveFirmwarePlan(chipName, fetcher = fetch, preferredHardware = null) {
  const normalized = String(chipName ?? "").trim().toUpperCase();
  const catalog = await loadFirmwareCatalog(fetcher);
  const supportsChip = (candidate) => candidate.chipNames.includes(normalized) || candidate.chipPrefixes.some((prefix) => normalized.startsWith(prefix));
  const release = preferredHardware
    ? catalog.find((candidate) => candidate.hardware === preferredHardware && supportsChip(candidate))
    : catalog.find((candidate) => candidate.hardware !== "ANTS-Make-AM-036" && supportsChip(candidate)) ?? catalog.find(supportsChip);
  if (preferredHardware && !release) throw new Error(`The connected ${chipName || "chip"} does not match ${preferredHardware}.`);
  if (!release) throw new Error(`Talos does not yet have firmware for ${chipName || "this chip"}.`);
  return release;
}
